import { useState, useEffect } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, X, Network, Server, Globe, Cloud, Lightbulb, 
  ArrowRight, Phone, Mail, MapPin, ChevronDown,
  Users, Target, Eye, Rocket, Code, Shield,
  Zap, MessageCircle, Package, Headphones, Settings,
  ShoppingBag, Edit, Trash2, Plus, Check, XCircle, 
  Search, Filter, Send, ArrowLeft, LogOut, Inbox,
  Building2, Factory, HardHat, Wrench, Battery, Cpu,
  CircuitBoard, Gauge, ShieldCheck, Clock, CheckCircle2
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Navigation Component
const Navigation = ({ activeSection, currentPage, setCurrentPage }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { id: "home", label: "Home" },
    { id: "about", label: "About" },
    { id: "services", label: "Services" },
    { id: "clients", label: "Clients" },
    { id: "contact", label: "Contact" },
  ];

  const scrollToSection = (id) => {
    if (currentPage !== "home") {
      setCurrentPage("home");
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "py-6"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <motion.button
          onClick={() => { setCurrentPage("home"); window.scrollTo({ top: 0 }); }}
          className="text-2xl font-bold tracking-tight"
          whileHover={{ scale: 1.05 }}
          data-testid="logo-link"
        >
          <span className="gradient-text">Infini</span>
          <span className="text-white">sys</span>
        </motion.button>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`nav-link text-sm font-medium transition-colors ${
                (currentPage === item.id || (currentPage === "home" && activeSection === item.id))
                  ? "text-[#00F0FF]" 
                  : "text-[#A1A1AA] hover:text-white"
              }`}
              data-testid={`nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(0, 240, 255, 0.4)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollToSection("contact")}
            className="btn-shine bg-[#00F0FF] text-black px-5 py-2 rounded-full text-sm font-semibold"
            data-testid="nav-cta-button"
          >
            Get Started
          </motion.button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden text-white p-2"
          data-testid="mobile-menu-toggle"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass mt-2 mx-4 rounded-2xl overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="p-4 flex flex-col gap-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-left px-4 py-3 rounded-lg transition-colors ${
                    (currentPage === item.id || (currentPage === "home" && activeSection === item.id))
                      ? "bg-[#00F0FF]/10 text-[#00F0FF]"
                      : "text-[#A1A1AA] hover:bg-white/5"
                  }`}
                  data-testid={`mobile-nav-${item.id}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Stats Counter Component
const StatsCounter = ({ value, label }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    const target = parseInt(value);
    const duration = 2000;
    const step = target / (duration / 16);
    let current = 0;
    
    const timer = setInterval(() => {
      current += step;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, 16);
    
    return () => clearInterval(timer);
  }, [value]);
  
  return (
    <div className="text-center">
      <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-white drop-shadow-lg">
        {count}+
      </div>
      <div className="text-xs md:text-sm text-[#A1A1AA]">{label}</div>
    </div>
  );
};

// Hero Section
const HeroSection = () => {
  const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bdd6c2e7-3740-4811-84d2-c9f0c0a3f53d/artifacts/9hvug00f_logo.png";
  
  const stats = [
    { value: "4", label: "Years Experience" },
    { value: "50", label: "Projects Completed" },
    { value: "15", label: "Happy Customers" },
  ];
  
  return (
    <section
      id="home"
      className="hero-bg min-h-screen flex flex-col relative"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2070&auto=format&fit=crop')`,
      }}
      data-testid="hero-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 flex-grow flex items-center pt-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center w-full">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            <span className="mono text-[#00F0FF] text-sm tracking-wider uppercase mb-4 block">
              Making Technology Work for People & Business
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold mb-6 leading-tight">
              Welcome to{" "}
              <span className="gradient-text">Infinisys</span>
            </h1>
            <p className="text-[#A1A1AA] text-base md:text-lg max-w-xl mx-auto lg:mx-0 mb-8">
              Empowering businesses through cutting-edge technology, streamlining processes and driving success with innovative IT infrastructure solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
              <motion.a
                href="#services"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white/80 bg-white/10 backdrop-blur-sm text-white px-6 py-3 rounded-lg font-bold shadow-lg hover:bg-white/20 transition-all flex items-center justify-center gap-2"
                data-testid="hero-cta-solutions"
              >
                EXPLORE SOLUTIONS
              </motion.a>
              <motion.a
                href="#contact"
                whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(0, 240, 255, 0.5)" }}
                whileTap={{ scale: 0.95 }}
                className="btn-shine bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-white px-6 py-3 rounded-lg font-bold shadow-lg"
                data-testid="hero-cta-primary"
              >
                GET STARTED
              </motion.a>
            </div>
            
            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="grid grid-cols-3 gap-6 max-w-sm mx-auto lg:mx-0"
              data-testid="hero-stats"
            >
              {stats.map((stat, index) => (
                <StatsCounter key={index} value={stat.value} label={stat.label} />
              ))}
            </motion.div>
          </motion.div>
          
          {/* Right - Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex justify-center items-center"
          >
            <motion.img
              src={COMPANY_LOGO}
              alt="Infinisys Technologies Logo"
              className="w-64 md:w-80 lg:w-96 animate-float"
              data-testid="hero-company-logo"
            />
          </motion.div>
        </div>
      </div>

      {/* Trusted By Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.6 }}
        className="relative z-10 py-8 bg-black/30 backdrop-blur-sm"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-4">
            <p className="text-sm text-slate-300 mb-2">Trusted by Industry Leaders</p>
            <div className="w-16 h-0.5 bg-gradient-to-r from-[#00F0FF] to-[#7000FF] mx-auto"></div>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-8">
            {["Google", "AWS", "Cisco", "HP", "Hikvision", "3C3"].map((partner, index) => (
              <div
                key={index}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-all"
              >
                <span className="text-lg md:text-xl font-semibold text-white/60 hover:text-white transition-colors">
                  {partner}
                </span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

// Digitalize & Accelerate Section
const DigitalizeSection = () => {
  const offerings = [
    {
      title: "DESIGN",
      description: "Proven Products & Solutions",
      color: "from-purple-600 to-purple-700",
      hoverColor: "hover:from-purple-700 hover:to-purple-800"
    },
    {
      title: "DEPLOY",
      description: "Specialized Deployment & Consulting Services",
      color: "from-red-500 to-red-600",
      hoverColor: "hover:from-red-600 hover:to-red-700"
    },
    {
      title: "SUPPORT",
      description: "Optimise & Manage Solutions",
      color: "from-teal-500 to-teal-600",
      hoverColor: "hover:from-teal-600 hover:to-teal-700"
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-[#0A0A0A] via-[#0D0D0D] to-[#0A0A0A]" data-testid="digitalize-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-3xl p-8 md:p-12 flex flex-col lg:flex-row items-center gap-10"
        >
          {/* Left - CTA */}
          <div className="lg:w-1/3 bg-gradient-to-br from-[#00F0FF]/20 to-[#7000FF]/20 rounded-2xl p-8 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Enable Intelligent <span className="gradient-text">Automation</span>
            </h3>
            <p className="text-[#A1A1AA] mb-6">
              Speak to us about solutions and technologies to accelerate your business
            </p>
            <div className="w-20 h-20 bg-[#00F0FF]/20 rounded-full flex items-center justify-center mx-auto animate-pulse-glow">
              <Zap className="w-10 h-10 text-[#00F0FF]" />
            </div>
          </div>

          {/* Right - Content */}
          <div className="lg:w-2/3 text-center lg:text-left">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              How can we be a part of your <span className="gradient-text">growth</span>?
            </h3>
            <p className="text-[#A1A1AA] mb-8">
              Accelerate your business potential with the innovative use of technology.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {offerings.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  className={`cursor-pointer bg-gradient-to-r ${item.color} ${item.hoverColor} p-5 rounded-xl shadow-lg text-center transition-all`}
                  data-testid={`digitalize-card-${index}`}
                >
                  <h4 className="text-lg font-bold text-white mb-2">{item.title}</h4>
                  <p className="text-sm text-white/90">{item.description}</p>
                </motion.div>
              ))}
            </div>
            <p className="text-sm text-[#52525B] italic mt-6">
              Supporting your digitalization journey throughout the transformation.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const features = [
    {
      icon: <Rocket className="w-8 h-8" />,
      title: "Our Story",
      description: "Founded in 2020, we started with a small team and a big mission to provide businesses with high-quality IT infrastructure services. Today, we serve businesses of all sizes across industries."
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Our Mission",
      description: "To empower businesses with technology solutions that enable them to achieve their goals, streamline operations, and stay ahead of the competition through personalized services."
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: "Our Vision",
      description: "To be the go-to provider of IT infrastructure services worldwide by continually innovating, investing in our team's expertise, and staying at the forefront of industry trends."
    }
  ];

  const values = [
    { icon: <Users className="w-6 h-6" />, title: "Our Team", description: "Experienced IT professionals passionate about delivering high-quality services and comprehensive IT support." },
    { icon: <Code className="w-6 h-6" />, title: "Our Culture", description: "Built on collaboration, innovation, and accountability. We foster open communication and creative thinking." },
    { icon: <Shield className="w-6 h-6" />, title: "Our Approach", description: "Consultative approach starting with understanding your business goals, then developing customized solutions." },
    { icon: <Lightbulb className="w-6 h-6" />, title: "Our Expertise", description: "Years of experience across industries, constantly expanding knowledge to better serve our clients." },
    { icon: <Server className="w-6 h-6" />, title: "Our Technology", description: "Leveraging latest technologies with partnerships with leading vendors for reliable IT infrastructure." },
    { icon: <Network className="w-6 h-6" />, title: "Our Partnerships", description: "Strategic partnerships with Cisco, Google, HP, and more for comprehensive IT solutions." }
  ];

  return (
    <section id="about" className="py-24 md:py-32 relative" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="mono text-[#00F0FF] text-sm tracking-wider uppercase">About Us</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
            Welcome To <span className="gradient-text">Infinisys</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-3xl mx-auto">
            Infinisys is a leading provider of IT infrastructure services, serving businesses of all sizes and industries. 
            Our team of experts is committed to delivering exceptional customer service and personalized solutions.
          </p>
        </motion.div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02, borderColor: "rgba(0, 240, 255, 0.5)" }}
              className="glass rounded-2xl p-8 transition-all duration-300"
              data-testid={`about-feature-${index}`}
            >
              <div className="text-[#00F0FF] mb-4">{feature.icon}</div>
              <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
              <p className="text-[#A1A1AA] leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Vision Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h3 className="text-3xl font-bold">Our <span className="gradient-text">Vision</span></h3>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ backgroundColor: "rgba(255, 255, 255, 0.08)" }}
              className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-6 transition-all duration-300"
              data-testid={`vision-card-${index}`}
            >
              <div className="flex items-start gap-4">
                <div className="text-[#00F0FF] p-2 bg-[#00F0FF]/10 rounded-lg">{value.icon}</div>
                <div>
                  <h4 className="font-semibold mb-2">{value.title}</h4>
                  <p className="text-[#A1A1AA] text-sm leading-relaxed">{value.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: <Network className="w-10 h-10" />,
      title: "Network Services",
      description: "Comprehensive network services including design, implementation, and management of wired and wireless networks. Optimize performance, improve security, and reduce downtime.",
      featured: true
    },
    {
      icon: <Server className="w-10 h-10" />,
      title: "Server Services",
      description: "Server installation, management, and support for various server types and operating systems. Optimize performance, ensure data security, and disaster recovery planning."
    },
    {
      icon: <Globe className="w-10 h-10" />,
      title: "Web Development",
      description: "Custom websites and e-commerce solutions tailored to your unique needs. We prioritize user experience, performance, and security for online success."
    },
    {
      icon: <Code className="w-10 h-10" />,
      title: "Web Maintenance",
      description: "Keep your websites running smoothly with routine maintenance, backups, updates, security scans, and troubleshooting services.",
      featured: true
    },
    {
      icon: <Cloud className="w-10 h-10" />,
      title: "Cloud Solutions",
      description: "Cloud migration, infrastructure management, and data security. We help you choose the right cloud solution with ongoing support."
    },
    {
      icon: <Lightbulb className="w-10 h-10" />,
      title: "IT Consulting",
      description: "Technology strategy development, IT infrastructure assessment, and project management to leverage technology for competitive advantage."
    }
  ];

  const electricalServices = [
    { title: "HT Electrical Projects", desc: "11kV/22kV/33kV Substation, Transformers, HT Panels & Cable Works" },
    { title: "Data Centre Power", desc: "Tier I-IV Infrastructure, UPS, DG Sets, Redundancy Design (N, N+1, 2N)" },
    { title: "LT Electrical Works", desc: "Panels, Bus Ducts, Cable Trays, Emergency Power & Metering" },
    { title: "O&M Services", desc: "24×7 Support, AMC, Energy Audits & Preventive Maintenance" }
  ];

  const industries = ["Data Centres", "IT Parks", "Manufacturing", "Commercial Buildings", "Healthcare", "Infrastructure"];

  return (
    <section id="services" className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="mono text-[#00F0FF] text-sm tracking-wider uppercase">What We Do</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
            Our <span className="gradient-text">Services</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-3xl mx-auto">
            We offer a wide range of IT infrastructure services, including network design, server management, 
            web development, cybersecurity, and IT consulting.
          </p>
        </motion.div>

        <div className="bento-grid mb-16">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              className={`service-card glass rounded-2xl p-8 transition-all duration-500 hover:border-[#00F0FF]/50 bento-item ${
                service.featured ? "border-[#7000FF]/30" : ""
              }`}
              data-testid={`service-card-${index}`}
            >
              <div className={`mb-6 inline-flex p-4 rounded-xl ${
                service.featured ? "bg-[#7000FF]/20 text-[#7000FF]" : "bg-[#00F0FF]/10 text-[#00F0FF]"
              }`}>
                {service.icon}
              </div>
              <h3 className="text-2xl font-semibold mb-4">{service.title}</h3>
              <p className="text-[#A1A1AA] leading-relaxed">{service.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Electrical Services Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-3xl p-8 md:p-10 border-[#7000FF]/30"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
            <div className="p-4 bg-[#7000FF]/20 rounded-xl w-fit">
              <Zap className="w-10 h-10 text-[#7000FF]" />
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-bold">Electrical <span className="gradient-text">Infrastructure</span></h3>
              <p className="text-[#A1A1AA]">HT/LT Electrical & Data Centre Power Solutions</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {electricalServices.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#0A0A0A] border border-[#27272A] hover:border-[#00F0FF]/50 p-5 rounded-xl transition-all"
              >
                <h4 className="font-semibold text-white mb-2">{item.title}</h4>
                <p className="text-sm text-[#A1A1AA]">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="text-sm text-[#A1A1AA]">Industries:</span>
            {industries.map((ind, i) => (
              <span key={i} className="px-3 py-1 bg-[#00F0FF]/10 text-[#00F0FF] text-xs rounded-full">{ind}</span>
            ))}
          </div>

          <div className="flex flex-wrap gap-4 text-sm text-[#A1A1AA]">
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-400" /> IS Standards & IE Rules Compliant</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-400" /> Uptime Institute Guidelines</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-400" /> 24×7 Mission-Critical Support</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Clients Section
const ClientsSection = () => {
  const clients = [
    {
      name: "Hayotrip",
      description: "A travel company specializing in travel-related services and products. Offering transportation, accommodations, tours, and activities for travelers.",
      detail: "Helping customers plan complex trips with personalized services and expertise."
    },
    {
      name: "Papawaqa",
      description: "A boutique hotel in Tainan City, Taiwan with 41 rooms decorated in modern minimalist style inspired by traditional architecture.",
      detail: "Located near popular attractions including Confucius Temple and Anping Old Street."
    },
    {
      name: "FII",
      description: "Foxconn Industrial Internet - a technology-driven company focused on intelligent manufacturing solutions with AI, IoT, and automation.",
      detail: "Providing end-to-end smart manufacturing services and digital factory design."
    },
    {
      name: "Foxconn Hon Hai",
      description: "One of the world's largest electronics manufacturers and global leader in advanced technology solutions.",
      detail: "Specializing in design, production, and assembly for major global brands."
    }
  ];

  return (
    <section id="clients" className="py-24 md:py-32" data-testid="clients-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="mono text-[#00F0FF] text-sm tracking-wider uppercase">Success Stories</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
            Our <span className="gradient-text">Clients</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-3xl mx-auto">
            We work with a diverse range of clients across industries. They trust us to provide 
            personalized solutions that help achieve their technology goals.
          </p>
        </motion.div>

        <div className="space-y-6">
          {clients.map((client, index) => (
            <motion.div
              key={client.name}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ borderColor: "rgba(0, 240, 255, 0.5)" }}
              className="client-card glass rounded-2xl p-8 md:p-10 grid md:grid-cols-3 gap-6 items-center transition-all duration-300"
              data-testid={`client-card-${index}`}
            >
              <div className="md:col-span-1">
                <div className="text-4xl md:text-5xl font-bold gradient-text mb-2">{client.name}</div>
                <span className="mono text-[#52525B] text-xs">TRUSTED PARTNER</span>
              </div>
              <div className="md:col-span-2">
                <p className="text-[#EDEDED] mb-3">{client.description}</p>
                <p className="text-[#A1A1AA] text-sm">{client.detail}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Partners Section
const PartnersSection = () => {
  const partners = ["Google", "AWS", "3C3", "Hikvision", "FaceMe", "PlayGPS", "IronYun", "TVT"];

  return (
    <section id="partners" className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="partners-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="mono text-[#00F0FF] text-sm tracking-wider uppercase">Collaborations</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
            Our <span className="gradient-text">Partners</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-3xl mx-auto">
            Collaborating with industry leaders to deliver innovative solutions. 
            Our partners help us stay ahead and deliver exceptional value.
          </p>
        </motion.div>

        {/* Logo Marquee */}
        <div className="relative overflow-hidden py-8">
          <div className="flex animate-marquee">
            {[...partners, ...partners].map((partner, index) => (
              <div
                key={`${partner}-${index}`}
                className="flex-shrink-0 mx-8 md:mx-12"
              >
                <div className="glass px-8 py-4 rounded-xl hover:border-[#00F0FF]/50 transition-all duration-300">
                  <span className="text-2xl md:text-3xl font-bold text-[#52525B] hover:text-white transition-colors">
                    {partner}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  /*const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSubmitted(true);
    setIsSubmitting(false);
    setFormData({ name: "", email: "", message: "" });
  };*/

  const isEmailValid = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const handleSubmit = async (e) => {
  e.preventDefault();

  const { name, email, message } = formData;

  // Same validations as old JS
  if (!name || !email || !message) {
    alert("Fill all the required fields");
    return;
  }

  if (!isEmailValid(email)) {
    alert("Please enter valid mail ID");
    return;
  }

  setIsSubmitting(true);

  try {
    const formDataToSend = new FormData();
    formDataToSend.append("entered_name", name);
    formDataToSend.append("entered_email", email);
    formDataToSend.append("entered_message", message);

    const response = await fetch("class/send_mail_request.php", {
      method: "POST",
      body: formDataToSend,
    });

    const text = await response.text();
    const json = JSON.parse(text);

    if (json.status === "true") {
      setSubmitted(true);
      setFormData({ name: "", email: "", message: "" });
    } else {
      alert("Failed to send message");
    }
  } catch (err) {
    console.error(err);
    alert("Server error");
  } finally {
    setIsSubmitting(false);
  }
};

  return (
    <section id="contact" className="py-24 md:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="mono text-[#00F0FF] text-sm tracking-wider uppercase">Get In Touch</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
            Contact <span className="gradient-text">Us</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-3xl mx-auto">
            We would love to hear from you! Get in touch to learn more about our services 
            and how we can help your business achieve its technology goals.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {submitted ? (
              <div className="glass rounded-2xl p-10 text-center">
                <div className="text-[#00F0FF] text-6xl mb-4">✓</div>
                <h3 className="text-2xl font-bold mb-2">Thank You!</h3>
                <p className="text-[#A1A1AA]">We've received your message and will get back to you soon.</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-[#00F0FF] hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="form-input w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-5 py-4 text-white placeholder-[#52525B] transition-all duration-300"
                    placeholder="Your name"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="form-input w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-5 py-4 text-white placeholder-[#52525B] transition-all duration-300"
                    placeholder="your@email.com"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Message</label>
                  <textarea
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="form-input w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-5 py-4 text-white placeholder-[#52525B] transition-all duration-300 resize-none"
                    placeholder="Your message..."
                    data-testid="contact-message-input"
                  />
                </div>
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02, boxShadow: "0 0 30px rgba(0, 240, 255, 0.5)" }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-shine w-full bg-[#00F0FF] text-black py-4 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </motion.button>
              </form>
            )}
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="glass rounded-2xl p-8" data-testid="contact-info">
              <h3 className="text-xl font-semibold mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#00F0FF]/10 rounded-lg text-[#00F0FF]">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <p className="font-medium mb-1">Bangalore Office</p>
                    <p className="text-[#A1A1AA] text-sm">
                      No. 103, 1st Floor, Brigade Rubix,<br />
                      HiMI watch factory road, Yashwantpur,<br />
                      Bangalore - 560013
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#7000FF]/10 rounded-lg text-[#7000FF]">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <p className="font-medium mb-1">Chennai Office</p>
                    <p className="text-[#A1A1AA] text-sm">
                      Greeta Tower, Industrial Estate,<br />
                      Perungudi, OMR, Phase-I,<br />
                      Chennai, Tamilnadu 600096
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-[#00F0FF]/10 rounded-lg text-[#00F0FF]">
                    <Phone size={20} />
                  </div>
                  <div>
                    <p className="font-medium">+91 999 402 3512</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-[#7000FF]/10 rounded-lg text-[#7000FF]">
                    <Mail size={20} />
                  </div>
                  <div>
                    <p className="font-medium">service@infinisys.in</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Products Page Component
const ProductsPage = ({ setCurrentPage }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [inquiryForm, setInquiryForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [inquirySent, setInquirySent] = useState(false);

  const categories = [
    { id: "all", label: "All Products" },
    { id: "hardware", label: "IT Hardware" },
    { id: "software", label: "Software/Licenses" },
    { id: "services", label: "IT Services" },
  ];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesCategory = activeCategory === "all" || product.category === activeCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleInquiry = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: selectedProduct.id,
          product_name: selectedProduct.name,
          customer_name: inquiryForm.name,
          customer_email: inquiryForm.email,
          customer_phone: inquiryForm.phone,
          message: inquiryForm.message
        })
      });
      if (response.ok) {
        setInquirySent(true);
        setTimeout(() => {
          setSelectedProduct(null);
          setInquiryForm({ name: "", email: "", phone: "", message: "" });
          setInquirySent(false);
        }, 2000);
      }
    } catch (error) {
      console.error("Error sending inquiry:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24" data-testid="products-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <button
            onClick={() => setCurrentPage("home")}
            className="text-[#A1A1AA] hover:text-white mb-6 inline-flex items-center gap-2 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft size={18} /> Back to Home
          </button>
          <span className="mono text-[#00F0FF] text-sm tracking-wider uppercase block mb-4">Our Products</span>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            IT <span className="gradient-text">Solutions</span> & Products
          </h1>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            Explore our range of IT hardware, software licenses, and professional services.
          </p>
        </motion.div>

        {/* Search & Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52525B]" size={20} />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl pl-12 pr-4 py-3 text-white placeholder-[#52525B] focus:border-[#00F0FF] transition-colors"
              data-testid="product-search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  activeCategory === cat.id
                    ? "bg-[#00F0FF] text-black"
                    : "bg-[#0A0A0A] text-[#A1A1AA] border border-[#27272A] hover:border-[#00F0FF]"
                }`}
                data-testid={`category-filter-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-[#00F0FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#A1A1AA]">Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-[#52525B] mx-auto mb-4" />
            <p className="text-[#A1A1AA]">No products found</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.02 }}
                className="glass rounded-2xl overflow-hidden group"
                data-testid={`product-card-${index}`}
              >
                <div className="h-48 overflow-hidden relative">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      product.category === "hardware" ? "bg-blue-500/80" :
                      product.category === "software" ? "bg-purple-500/80" : "bg-teal-500/80"
                    } text-white`}>
                      {product.category === "hardware" ? "Hardware" :
                       product.category === "software" ? "Software" : "Service"}
                    </span>
                  </div>
                  {product.featured && (
                    <div className="absolute top-3 right-3">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#00F0FF] text-black">
                        Featured
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2 text-white">{product.name}</h3>
                  <p className="text-[#A1A1AA] text-sm mb-4 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold gradient-text">
                      ₹{product.price.toLocaleString('en-IN')}
                      {product.category === "software" && <span className="text-sm text-[#A1A1AA]">/user/mo</span>}
                    </span>
                    <span className={`text-sm ${product.in_stock ? "text-green-400" : "text-red-400"}`}>
                      {product.in_stock ? "Available" : "Not Available"}
                    </span>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedProduct(product)}
                    className="w-full bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
                    data-testid={`inquiry-btn-${product.id}`}
                  >
                    <Send size={18} /> Inquire Now
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Inquiry Modal */}
      <AnimatePresence>
        {selectedProduct && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => !submitting && setSelectedProduct(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#0A0A0A] border border-[#27272A] rounded-2xl p-8 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="inquiry-modal"
            >
              {inquirySent ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Inquiry Sent!</h3>
                  <p className="text-[#A1A1AA]">We'll get back to you shortly.</p>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="text-xl font-bold">Inquire About</h3>
                      <p className="text-[#00F0FF]">{selectedProduct.name}</p>
                    </div>
                    <button
                      onClick={() => setSelectedProduct(null)}
                      className="text-[#A1A1AA] hover:text-white"
                    >
                      <X size={24} />
                    </button>
                  </div>
                  <form onSubmit={handleInquiry} className="space-y-4">
                    <input
                      type="text"
                      required
                      placeholder="Your Name"
                      value={inquiryForm.name}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                      className="w-full bg-[#050505] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder-[#52525B]"
                      data-testid="inquiry-name-input"
                    />
                    <input
                      type="email"
                      required
                      placeholder="Email Address"
                      value={inquiryForm.email}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                      className="w-full bg-[#050505] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder-[#52525B]"
                      data-testid="inquiry-email-input"
                    />
                    <input
                      type="tel"
                      placeholder="Phone (Optional)"
                      value={inquiryForm.phone}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, phone: e.target.value })}
                      className="w-full bg-[#050505] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder-[#52525B]"
                      data-testid="inquiry-phone-input"
                    />
                    <textarea
                      required
                      rows={3}
                      placeholder="Your message or requirements..."
                      value={inquiryForm.message}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                      className="w-full bg-[#050505] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder-[#52525B] resize-none"
                      data-testid="inquiry-message-input"
                    />
                    <motion.button
                      type="submit"
                      disabled={submitting}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full bg-[#00F0FF] text-black py-3 rounded-xl font-semibold disabled:opacity-50"
                      data-testid="inquiry-submit-btn"
                    >
                      {submitting ? "Sending..." : "Send Inquiry"}
                    </motion.button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Admin Page Component
const AdminPage = ({ setCurrentPage }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [activeTab, setActiveTab] = useState("products");
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: "", description: "", price: "", image_url: "", category: "hardware", in_stock: true, stock_quantity: 0, featured: false
  });
  const [saving, setSaving] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: {
          "Authorization": "Basic " + btoa(`${credentials.username}:${credentials.password}`)
        }
      });
      if (response.ok) {
        setIsAuthenticated(true);
        localStorage.setItem("adminAuth", btoa(`${credentials.username}:${credentials.password}`));
        fetchData();
      } else {
        setLoginError("Invalid credentials");
      }
    } catch (error) {
      setLoginError("Login failed");
    }
  };

  const getAuthHeader = () => {
    const auth = localStorage.getItem("adminAuth");
    return { "Authorization": `Basic ${auth}` };
  };

  const fetchData = async () => {
    try {
      const [productsRes, inquiriesRes] = await Promise.all([
        fetch(`${API_URL}/api/products`),
        fetch(`${API_URL}/api/admin/inquiries`, { headers: getAuthHeader() })
      ]);
      if (productsRes.ok) setProducts(await productsRes.json());
      if (inquiriesRes.ok) setInquiries(await inquiriesRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...productForm,
        price: parseFloat(productForm.price),
        stock_quantity: parseInt(productForm.stock_quantity)
      };
      
      const url = editingProduct
        ? `${API_URL}/api/admin/products/${editingProduct.id}`
        : `${API_URL}/api/admin/products`;
      
      const response = await fetch(url, {
        method: editingProduct ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        fetchData();
        resetForm();
      }
    } catch (error) {
      console.error("Error saving product:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await fetch(`${API_URL}/api/admin/products/${productId}`, {
        method: "DELETE",
        headers: getAuthHeader()
      });
      fetchData();
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  const handleSeedProducts = async () => {
    if (!window.confirm("This will replace all existing products with sample data. Continue?")) return;
    try {
      await fetch(`${API_URL}/api/admin/seed-products`, {
        method: "POST",
        headers: getAuthHeader()
      });
      fetchData();
    } catch (error) {
      console.error("Error seeding products:", error);
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setProductForm({
      name: "", description: "", price: "", image_url: "", category: "hardware", in_stock: true, stock_quantity: 0, featured: false
    });
  };

  const editProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      image_url: product.image_url,
      category: product.category,
      in_stock: product.in_stock,
      stock_quantity: product.stock_quantity,
      featured: product.featured
    });
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    setIsAuthenticated(false);
    setCredentials({ username: "", password: "" });
  };

  useEffect(() => {
    const auth = localStorage.getItem("adminAuth");
    if (auth) {
      setIsAuthenticated(true);
      fetchData();
    }
  }, []);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl p-8 max-w-md w-full"
          data-testid="admin-login-form"
        >
          <div className="text-center mb-8">
            <Settings className="w-12 h-12 text-[#00F0FF] mx-auto mb-4" />
            <h1 className="text-2xl font-bold">Admin Login</h1>
            <p className="text-[#A1A1AA] text-sm">Enter your credentials to continue</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="text"
              required
              placeholder="Username"
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-4 py-3 text-white"
              data-testid="admin-username-input"
            />
            <input
              type="password"
              required
              placeholder="Password"
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-4 py-3 text-white"
              data-testid="admin-password-input"
            />
            {loginError && <p className="text-red-400 text-sm">{loginError}</p>}
            <button
              type="submit"
              className="w-full bg-[#00F0FF] text-black py-3 rounded-xl font-semibold"
              data-testid="admin-login-btn"
            >
              Login
            </button>
          </form>
          <button
            onClick={() => setCurrentPage("home")}
            className="w-full mt-4 text-[#A1A1AA] hover:text-white transition-colors"
          >
            Back to Website
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Admin <span className="gradient-text">Dashboard</span></h1>
            <p className="text-[#A1A1AA]">Manage products and inquiries</p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={() => setCurrentPage("products")}
              className="text-[#A1A1AA] hover:text-white transition-colors"
            >
              View Store
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveTab("products")}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === "products" ? "bg-[#00F0FF] text-black" : "bg-[#0A0A0A] text-[#A1A1AA] border border-[#27272A]"
            }`}
            data-testid="admin-tab-products"
          >
            <Package className="inline mr-2" size={18} /> Products ({products.length})
          </button>
          <button
            onClick={() => setActiveTab("inquiries")}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === "inquiries" ? "bg-[#00F0FF] text-black" : "bg-[#0A0A0A] text-[#A1A1AA] border border-[#27272A]"
            }`}
            data-testid="admin-tab-inquiries"
          >
            <Inbox className="inline mr-2" size={18} /> Inquiries ({inquiries.length})
          </button>
        </div>

        {/* Products Tab */}
        {activeTab === "products" && (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Product Form */}
            <div className="lg:col-span-1">
              <div className="glass rounded-2xl p-6 sticky top-28">
                <h3 className="text-xl font-bold mb-4">
                  {editingProduct ? "Edit Product" : "Add Product"}
                </h3>
                <form onSubmit={handleSaveProduct} className="space-y-4">
                  <input
                    type="text"
                    required
                    placeholder="Product Name"
                    value={productForm.name}
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-2 text-white text-sm"
                    data-testid="product-name-input"
                  />
                  <textarea
                    required
                    rows={3}
                    placeholder="Description"
                    value={productForm.description}
                    onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-2 text-white text-sm resize-none"
                    data-testid="product-description-input"
                  />
                  <input
                    type="number"
                    required
                    step="0.01"
                    placeholder="Price (₹)"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-2 text-white text-sm"
                    data-testid="product-price-input"
                  />
                  <input
                    type="url"
                    required
                    placeholder="Image URL"
                    value={productForm.image_url}
                    onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-2 text-white text-sm"
                    data-testid="product-image-input"
                  />
                  <select
                    value={productForm.category}
                    onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-2 text-white text-sm"
                    data-testid="product-category-select"
                  >
                    <option value="hardware">IT Hardware</option>
                    <option value="software">Software/License</option>
                    <option value="services">IT Services</option>
                  </select>
                  <input
                    type="number"
                    placeholder="Stock Quantity"
                    value={productForm.stock_quantity}
                    onChange={(e) => setProductForm({ ...productForm, stock_quantity: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-2 text-white text-sm"
                    data-testid="product-stock-input"
                  />
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={productForm.in_stock}
                        onChange={(e) => setProductForm({ ...productForm, in_stock: e.target.checked })}
                        className="rounded"
                        data-testid="product-instock-checkbox"
                      />
                      Available
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={productForm.featured}
                        onChange={(e) => setProductForm({ ...productForm, featured: e.target.checked })}
                        className="rounded"
                        data-testid="product-featured-checkbox"
                      />
                      Featured
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex-1 bg-[#00F0FF] text-black py-2 rounded-lg font-semibold text-sm disabled:opacity-50"
                      data-testid="product-save-btn"
                    >
                      {saving ? "Saving..." : editingProduct ? "Update" : "Add Product"}
                    </button>
                    {editingProduct && (
                      <button
                        type="button"
                        onClick={resetForm}
                        className="px-4 py-2 bg-[#27272A] text-white rounded-lg text-sm"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
                <button
                  onClick={handleSeedProducts}
                  className="w-full mt-4 py-2 border border-[#27272A] text-[#A1A1AA] rounded-lg text-sm hover:bg-[#27272A] transition-colors"
                  data-testid="seed-products-btn"
                >
                  Seed Sample Products
                </button>
              </div>
            </div>

            {/* Products List */}
            <div className="lg:col-span-2 space-y-4">
              {products.length === 0 ? (
                <div className="glass rounded-2xl p-12 text-center">
                  <Package className="w-16 h-16 text-[#52525B] mx-auto mb-4" />
                  <p className="text-[#A1A1AA]">No products yet. Add your first product or seed sample data.</p>
                </div>
              ) : (
                products.map((product) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="glass rounded-xl p-4 flex gap-4 items-center"
                    data-testid={`admin-product-${product.id}`}
                  >
                    <img src={product.image_url} alt={product.name} className="w-20 h-20 object-cover rounded-lg" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold truncate">{product.name}</h4>
                        {product.featured && (
                          <span className="px-2 py-0.5 bg-[#00F0FF]/20 text-[#00F0FF] text-xs rounded">Featured</span>
                        )}
                      </div>
                      <p className="text-[#A1A1AA] text-sm truncate">{product.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="text-[#00F0FF] font-bold">₹{product.price.toLocaleString('en-IN')}</span>
                        <span className={product.in_stock ? "text-green-400" : "text-red-400"}>
                          {product.in_stock ? `Available (${product.stock_quantity})` : "Not Available"}
                        </span>
                        <span className="text-[#52525B] capitalize">{product.category}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => editProduct(product)}
                        className="p-2 bg-[#27272A] rounded-lg hover:bg-[#3f3f46] transition-colors"
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Inquiries Tab */}
        {activeTab === "inquiries" && (
          <div className="space-y-4">
            {inquiries.length === 0 ? (
              <div className="glass rounded-2xl p-12 text-center">
                <Inbox className="w-16 h-16 text-[#52525B] mx-auto mb-4" />
                <p className="text-[#A1A1AA]">No inquiries yet.</p>
              </div>
            ) : (
              inquiries.map((inquiry) => (
                <motion.div
                  key={inquiry.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="glass rounded-xl p-6"
                  data-testid={`inquiry-${inquiry.id}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="font-semibold text-[#00F0FF]">{inquiry.product_name}</h4>
                      <p className="text-sm text-[#A1A1AA]">
                        From: {inquiry.customer_name} ({inquiry.customer_email})
                        {inquiry.customer_phone && ` • ${inquiry.customer_phone}`}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs ${
                      inquiry.status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
                      inquiry.status === "contacted" ? "bg-blue-500/20 text-blue-400" : "bg-green-500/20 text-green-400"
                    }`}>
                      {inquiry.status}
                    </span>
                  </div>
                  <p className="text-[#EDEDED] mb-4">{inquiry.message}</p>
                  <p className="text-xs text-[#52525B]">
                    {new Date(inquiry.created_at).toLocaleString()}
                  </p>
                </motion.div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 border-t border-[#27272A]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-2xl font-bold">
            <span className="gradient-text">Infini</span>
            <span className="text-white">sys</span>
          </div>
          <p className="text-[#52525B] text-sm">
            © {new Date().getFullYear()} Infinisys. All rights reserved.
          </p>
          <a
            href="https://infinisys.in"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#A1A1AA] hover:text-[#00F0FF] transition-colors text-sm"
          >
            infinisys.in
          </a>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => {
  return (
    <motion.a
      href="https://wa.me/919994023512"
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#20BA5A] text-white p-4 rounded-full shadow-2xl transition-all group"
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      aria-label="Contact us on WhatsApp"
      data-testid="whatsapp-button"
    >
      <MessageCircle className="w-7 h-7" />
      <div className="absolute inset-0 rounded-full bg-[#25D366] opacity-30 animate-ping"></div>
      
      {/* Tooltip */}
      <div className="absolute bottom-full right-0 mb-3 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg">
        Chat with us on WhatsApp
        <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
      </div>
    </motion.a>
  );
};

// Main App
function App() {
  const [activeSection, setActiveSection] = useState("home");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState("home");

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Check URL for admin page
    if (window.location.hash === "#admin") {
      setCurrentPage("admin");
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (currentPage !== "home") return;
      const sections = ["home", "about", "services", "clients", "partners", "contact"];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [currentPage]);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Loading Screen */}
      <AnimatePresence>
        {loading && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#050505] flex items-center justify-center"
            data-testid="loading-screen"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 1.2, opacity: 0 }}
              className="text-center"
            >
              <svg className="infinity-loader mx-auto mb-6" viewBox="-50 -25 100 50">
                <path d="M-35 0C-35 -20 -15 -20 0 0C15 20 35 20 35 0C35 -20 15 -20 0 0C-15 20 -35 20 -35 0Z" />
              </svg>
              <div className="text-2xl font-bold">
                <span className="gradient-text">Infini</span>
                <span className="text-white">sys</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {currentPage !== "admin" && (
        <Navigation activeSection={activeSection} currentPage={currentPage} setCurrentPage={setCurrentPage} />
      )}
      
      {currentPage === "home" && (
        <main>
          <HeroSection />
          <DigitalizeSection />
          <AboutSection />
          <ServicesSection />
          <ClientsSection />
          <PartnersSection />
          <ContactSection />
        </main>
      )}
      
      {currentPage === "admin" && (
        <AdminPage setCurrentPage={setCurrentPage} />
      )}
      
      {currentPage !== "admin" && <Footer />}
      <WhatsAppButton />
    </div>
  );
}

export default App;
